<template>
  <div class="w-full">
    <LcButton
      :aria-haspopup="hasIncompletePlays || hasNoCompletePlays ? 'dialog' : 'false'"
      class="button w-full"
      icon="arrow-right"
      icon-position="right"
      no-padding
      size="large"
      @click="redirectToOptions"
    >
      {{ $t('lottery.navbar.nextstep', { count: playsToReview }) }}
    </LcButton>
  </div>
</template>

<script lang="ts" setup>
import { KnownProductType } from '#api/shared/@types/generated/backend/graphql-schema-types'
import { LotteryErrorModal, LotteryIncompletePlaysModal } from '#components'

const { logTrackingError } = useTrackingErrorLog()

const { navigateToLocalized } = useRouterUtils()
const { openModal } = useModal()
const slots = useSlots()

const {
  completePlays,
  currentPrice,
  fillPlaysWithQuickPick,
  firstIncompleteAndDirtyPlayId,
  hasIncompletePlays,
  hasNoCompletePlays,
  incompleteAndDirtyPlays,
  isAllowed,
  lotteryId,
  plays,
  ticketType,
  visiblePlayId,
} = usePlaySlip()
const route = useRoute()
const { createECommerceEventData } = useTrackingData()
const { pushAddToCartEvent } = useTracking()

const playsToReview = computed(
  () => completePlays.value.length + incompleteAndDirtyPlays.value.length || 1,
)

const openIncompletePlaysModal = () =>
  openModal({
    component: LotteryIncompletePlaysModal,
    onCancel: fillIncompletePlaysWithQuickPick,
    onConfirm: () => {
      visiblePlayId.value = firstIncompleteAndDirtyPlayId.value
    },
    props: { count: playsToReview.value },
    slots,
  })

const openOutOfBoundsModal = () =>
  openModal({
    component: LotteryErrorModal,
    props: { error: new Error('validation.plays.outofbounds') },
  })

const fillIncompletePlaysWithQuickPick = () => {
  if (hasNoCompletePlays.value && plays.value.length === 1) {
    fillWithQuickPick(plays.value[0])
  }

  incompleteAndDirtyPlays.value.forEach(fillWithQuickPick)
  navigateToLocalized(`/${lotteryId.toLowerCase()}/options`)
}

const fillWithQuickPick = ({ id, perPlayAddOn, stake }: PlaySlipPlay) => {
  fillPlaysWithQuickPick([id], {
    betType: perPlayAddOn?.value,
    stake: stake,
  })
}

const generateEventData = async () =>
  await createECommerceEventData({
    currentPrice: currentPrice.value,
    lotteryId,
    plays: plays.value,
    ticketType: ticketType.value,
    type: KnownProductType.Lottery,
  })

const redirectToOptions = async () => {
  if (hasIncompletePlays.value || hasNoCompletePlays.value) {
    openIncompletePlaysModal()
    return
  }

  if (!isAllowed.value) {
    openOutOfBoundsModal()
    return
  }

  try {
    pushAddToCartEvent(await generateEventData(), true)
  } catch (err) {
    logTrackingError('add_to_cart_by_next_step', err)
  }

  const backroute = route.query?.backroute

  await navigateToLocalized({
    path: `/${lotteryId.toLowerCase()}/options`,
    query: {
      backroute: typeof backroute === 'string' ? backroute : undefined,
    },
  })
}
</script>
